import { FC, FormEvent, useState } from 'react'
import { Col, Row } from 'react-grid-system'
import styled from 'styled-components'
import { NormalFormPasswordField } from '../molecules'
import { Button } from '../atoms'
import { useUserLanguage } from '@/customHooks/useUserLanguage'
import { ChangePasswordData } from '@/custom-types'
import textsData from '@/textData/ChangePasswordFormTexts.json'
import { ChangePasswordMutation, useChangePasswordMutation } from '@/graphql/generated'
import { FetchResult } from '@apollo/client'
import { useErrorHandlerContext, useOpeningElementsControllerContext } from '@/customHooks'

type ChangePasswordFormProps = {
  onCancelAction?: () => void
}

type InternalChangePasswordData = ChangePasswordData & {
  confirmPassword: string
}

type WarnText = {
  text1: boolean,
  text2: boolean,
}
const FormWrapper = styled.form`
  & > legend {
    font-size: ${({ theme }) => theme.typography.fontSize.s4};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    margin-bottom: 2.5rem;
  }
`

const ButtonBox = styled(Col)`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  justify-content: center;
  align-items: center;
  & > button {
    font-weight: 500;
    min-width: 200px;
  }
`

const WarnText = styled.p<{ $visible: boolean }>`
  color: ${({ theme }) => theme.colors.warn};
  visibility: ${({ $visible }) => $visible ? 'visible' : 'hidden'};
`

export const ChangePasswordForm: FC<ChangePasswordFormProps> = ({ onCancelAction }) => {
  const [formState, setFormState] = useState<InternalChangePasswordData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  const { changePasswordModalClose } = useOpeningElementsControllerContext()

  const { open: errorHandle } = useErrorHandlerContext()

  const [isWarnTextVisible, setIsWarnTextVisible] = useState<WarnText>({ text1: false, text2: false})

  const userLanguage = useUserLanguage()

  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const [changePassword, { loading }] = useChangePasswordMutation()

  const onSuccessHandle = (response: FetchResult<ChangePasswordMutation>) => {
    if (response.data?.changePassword.paswordWasUpdated) changePasswordModalClose()
    else if (response.data?.changePassword.oldPasswordWrong) {
      setIsWarnTextVisible({
        text1: false,
        text2: true,
      })
    }
  }

  const onSubmitHandle = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (formState.newPassword !== formState.confirmPassword) {
      setIsWarnTextVisible((prev) => ({ ...prev, text1: true }))
      return
    }
    console.log(formState)
    
    changePassword({ variables: {
      oldPassword: formState.currentPassword,
      newPassword: formState.newPassword,
    }})
      .then(onSuccessHandle)
      .catch(errorHandle)
  }

  return (
    <FormWrapper onSubmit={onSubmitHandle}>
      <legend>{texts.title}</legend>
      <Row>
        <Col>
          <NormalFormPasswordField
            placeholder={texts.currentPassword.placeholder}
            name={texts.currentPassword.label.toLowerCase().replace(' ', '-')}
            action={(value) => setFormState(( prev ) => ({ ...prev, currentPassword: value }))}
            label={texts.currentPassword.label}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormPasswordField
            placeholder={texts.newPassword.placeholder}
            name={texts.newPassword.label.toLowerCase().replace(' ','-')}
            action={(value) => setFormState(( prev ) => ({ ...prev, newPassword: value }))}
            label={texts.newPassword.label}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <NormalFormPasswordField
            placeholder={texts.confirmPassword.placeholder}
            name={texts.confirmPassword.label.toLowerCase().replace(' ', '-')}
            action={(value) => setFormState(( prev ) => ({ ...prev, confirmPassword: value }))}
            label={texts.confirmPassword.label}
          />
        </Col>
        <Col>
          <WarnText $visible={isWarnTextVisible.text1 || isWarnTextVisible.text2}>
            {isWarnTextVisible.text1 ? texts.confirmPassword.warnNonEqualPassword : null}
            {isWarnTextVisible.text2 ? texts.confirmPassword.warnWrongPassword : null}
          </WarnText>
        </Col>
      </Row>
      <Row>
        <ButtonBox>
          {
            onCancelAction
            ? (
              <Button
                onClick={onCancelAction}
                secondary
              >
                {texts.buttons.cancel}
              </Button>
            )
            : null
          }
          <Button submit isLoading={loading}>
            {texts.buttons.confirm}
          </Button>
        </ButtonBox>
      </Row>
    </FormWrapper>
  )
}
